// src/components/Layout/Navbar.js
import React, { useState, useContext, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import {
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  Tooltip,
  Divider,
  alpha,
  Menu,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PeopleIcon from '@mui/icons-material/People';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import WorkIcon from '@mui/icons-material/Work';

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [signUpMenuAnchor, setSignUpMenuAnchor] = useState(null);
  const { authData, setAuthData } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [scrolled, setScrolled] = useState(false);

  // Navigation items
  const items = [
    { text: 'Creators', to: '/creators', icon: <PeopleIcon /> },
    { text: 'Leaderboard', to: '/leaderboard', icon: <EmojiEventsIcon /> },
    { text: 'Reviews', to: '/reviews', icon: <StarIcon /> },
    { text: 'Profile', to: '/dashboard', icon: <AccountCircleIcon /> },
  ];

  // Handle scroll effect for AppBar
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const logout = () => {
    setAuthData(null);
    navigate('/login');
  };

  // Check if a nav item is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Handle sign up dropdown
  const handleSignUpClick = (event) => {
    setSignUpMenuAnchor(event.currentTarget);
  };

  const handleSignUpClose = () => {
    setSignUpMenuAnchor(null);
  };

  // Scroll to section functions
  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      // If not on home page, navigate to home first then scroll
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // If on home page, scroll directly
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    handleSignUpClose();
  };

  const isHomePage = location.pathname === '/';
  const showSignUpDropdown = !authData && isHomePage;

  return (
    <>
      <AppBar 
        position="fixed" 
        color="primary"
        elevation={scrolled ? 4 : 0}
        sx={{
          transition: 'all 0.3s ease',
          bgcolor: scrolled ? 'primary.main' : 'primary.main',
          backdropFilter: scrolled ? 'blur(8px)' : 'none',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, md: 4 }, py: 1 }}>
          {/* Mobile hamburger */}
          {!isMdUp && (
            <IconButton 
              edge="start" 
              color="inherit" 
              onClick={() => setOpen(true)}
              sx={{ 
                mr: 2,
                transition: 'transform 0.2s',
                '&:hover': { transform: 'scale(1.1)' }
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo */}
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              mr: 3,
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.05)' }
            }}
          >
            <Box
              component="img"
              src="/logo.svg"
              alt="OnlyStats Logo"
              sx={{ height: 36 }}
            />
          </Box>

          {/* Desktop menu */}
          {isMdUp && authData && (
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                gap: 1
              }}
            >
              {items.map((item) => {
                const active = isActive(item.to);
                return (
                  <Button
                    key={item.text}
                    component={RouterLink}
                    to={item.to}
                    color="inherit"
                    startIcon={item.icon}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    sx={{
                      position: 'relative',
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      fontWeight: active ? 600 : 500,
                      color: 'white',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 6,
                        left: '50%',
                        width: active ? '30%' : '0%',
                        height: 3,
                        bgcolor: 'white',
                        transition: 'all 0.3s ease',
                        transform: 'translateX(-50%)',
                        borderRadius: 4,
                      },
                      '&:hover::after': {
                        width: '30%',
                      },
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.common.white, 0.1),
                        transform: 'translateY(-2px)',
                      }
                    }}
                  >
                    {item.text}
                  </Button>
                );
              })}
            </Box>
          )}

          {/* Spacer for non-authenticated users */}
          {!authData && <Box sx={{ flexGrow: 1 }} />}

          {/* Auth buttons for non-authenticated users */}
          {!authData && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Always show Login button */}
              <Button 
                component={RouterLink} 
                to="/login"
                color="inherit"
                sx={{
                  fontWeight: 500,
                  borderRadius: 2,
                  px: 2,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.1),
                  },
                }}
              >
                Log In
              </Button>

              {/* Sign Up Dropdown (only on home page) or regular Sign Up button */}
              {showSignUpDropdown ? (
                <Box>
                  <Button
                    color="inherit"
                    onClick={handleSignUpClick}
                    endIcon={<KeyboardArrowDownIcon />}
                    sx={{
                      borderRadius: 2,
                      px: 2,
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.common.white, 0.1),
                      }
                    }}
                  >
                    Sign Up
                  </Button>
                  <Menu
                    anchorEl={signUpMenuAnchor}
                    open={Boolean(signUpMenuAnchor)}
                    onClose={handleSignUpClose}
                    PaperProps={{
                      sx: {
                        borderRadius: 2,
                        mt: 1,
                        minWidth: 200,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      }
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <MenuItem 
                      onClick={() => scrollToSection('how-it-works')}
                      sx={{ 
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        }
                      }}
                    >
                      <PersonAddIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                      Sign Up
                    </MenuItem>
                    <MenuItem 
                      onClick={() => scrollToSection('for-creators')}
                      sx={{ 
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.secondary.main, 0.1),
                        }
                      }}
                    >
                      <WorkIcon sx={{ mr: 1.5, color: 'secondary.main' }} />
                      Sign Up as Creator
                    </MenuItem>
                  </Menu>
                </Box>
              ) : (
                /* Regular Sign Up button for other pages */
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="contained"
                  color="secondary"
                  sx={{ 
                    px: 2,
                    py: 1,
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4,
                    },
                  }}
                >
                  Sign Up
                </Button>
              )}
            </Box>
          )}

          {/* Logout button */}
          {authData && (
            <Tooltip title="Logout">
              <Button 
                color="inherit" 
                onClick={logout} 
                startIcon={<LogoutIcon />}
                sx={{ 
                  ml: isMdUp ? 2 : 'auto',
                  borderRadius: 2,
                  px: 2,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.1),
                  }
                }}
              >
                {isMdUp ? 'Logout' : ''}
              </Button>
            </Tooltip>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer 
        anchor="left" 
        open={open} 
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            borderRadius: '0 16px 16px 0',
            boxShadow: 8
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Drawer header */}
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box
              component="img"
              src="/logo.svg"
              alt="OnlyStats Logo"
              sx={{ height: 32 }}
            />
            <IconButton onClick={() => setOpen(false)} edge="end" sx={{ borderRadius: 2 }}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Divider />
          
          {/* Navigation items */}
          <List sx={{ flexGrow: 1, pt: 2 }}>
            {/* Show navigation items only for authenticated users */}
            {authData && items.map((item) => {
              const active = isActive(item.to);
              return (
                <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    component={RouterLink}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    selected={active}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      transition: 'all 0.2s',
                      '&.Mui-selected': {
                        bgcolor: 'primary.light',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'primary.main',
                        },
                        '& .MuiListItemIcon-root': {
                          color: 'white',
                        }
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                    <ListItemText 
                      primary={item.text} 
                      primaryTypographyProps={{ 
                        fontWeight: active ? 600 : 500 
                      }} 
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
            
            {/* Auth options for mobile (when on home page and not authenticated) */}
            {showSignUpDropdown && (
              <>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    component={RouterLink}
                    to="/login"
                    onClick={() => setOpen(false)}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <AccountCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Log In" 
                      primaryTypographyProps={{ 
                        fontWeight: 500 
                      }} 
                    />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    onClick={() => {
                      setOpen(false);
                      scrollToSection('how-it-works');
                    }}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <PersonAddIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Sign Up" 
                      primaryTypographyProps={{ 
                        fontWeight: 500 
                      }} 
                    />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    onClick={() => {
                      setOpen(false);
                      scrollToSection('for-creators');
                    }}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.secondary.main, 0.1),
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <WorkIcon color="secondary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Sign Up as Creator" 
                      primaryTypographyProps={{ 
                        fontWeight: 500 
                      }} 
                    />
                  </ListItemButton>
                </ListItem>
              </>
            )}

            {/* Auth options for mobile (when not on home page and not authenticated) */}
            {!authData && !showSignUpDropdown && (
              <>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    component={RouterLink}
                    to="/login"
                    onClick={() => setOpen(false)}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <AccountCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Log In" 
                      primaryTypographyProps={{ 
                        fontWeight: 500 
                      }} 
                    />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    component={RouterLink}
                    to="/register"
                    onClick={() => setOpen(false)}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.secondary.main, 0.1),
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <PersonAddIcon color="secondary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Sign Up" 
                      primaryTypographyProps={{ 
                        fontWeight: 500 
                      }} 
                    />
                  </ListItemButton>
                </ListItem>
              </>
            )}
          </List>
          
          <Divider />
          
          {/* Logout button */}
          {authData && (
            <Box sx={{ p: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={<LogoutIcon />}
                onClick={logout}
                sx={{ borderRadius: 2, py: 1 }}
              >
                Logout
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>
      
      {/* Toolbar spacer */}
      <Toolbar />
    </>
  );
}
